import React, { useMemo, useState } from 'react';
import { Empty, ErrorText, Panel, labelFor, useChrome } from './ceoChrome';

const SORTS = [
  { id: 'score', label: 'Score' },
  { id: 'name', label: 'Name' },
  { id: 'invited', label: 'Homeowners' },
  { id: 'tasks', label: 'Tasks' },
];

function compare(sort, left, right) {
  if (sort === 'name') return left.name.localeCompare(right.name);
  if (sort === 'invited') return (right.homeownersInvited || 0) - (left.homeownersInvited || 0);
  if (sort === 'tasks') return (right.tasksCompleted || 0) - (left.tasksCompleted || 0);
  return (left.score || 0) - (right.score || 0);
}

export default function AgentHealthPanel({ colors, narrow, data, error }) {
  const chrome = useChrome(colors, narrow);
  const [sort, setSort] = useState('score');
  const agents = useMemo(() => {
    const rows = (data && data.agents) || [];
    return [...rows].sort((left, right) => compare(sort, left, right));
  }, [data, sort]);

  return (
    <div style={chrome.page} data-testid="ceo-health">
      <div>
        <h2 style={chrome.title}>Agent health</h2>
        <p style={chrome.sub}>A 0 to 100 score from logins, invited homeowners, active homeowners, completed tasks, projects sent, and trial time left.</p>
      </div>
      {error ? <ErrorText>{error}</ErrorText> : null}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: narrow ? '100%' : 220 }}>
        <span style={{ color: chrome.secondary, fontSize: 13 }}>Sort</span>
        <select aria-label="Sort agents" value={sort} onChange={(event) => setSort(event.target.value)} style={chrome.field}>
          {SORTS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
      </label>
      {data && data.loginSource === 'unavailable' ? <Empty chrome={chrome}>Login times are not connected, so that part of the score stays empty.</Empty> : null}
      {!data && !error ? <Empty chrome={chrome}>Loading…</Empty> : null}
      {data && agents.length === 0 ? <Empty chrome={chrome}>No agents yet.</Empty> : null}
      {agents.map((agent) => {
        const tone = labelFor(agent.label);
        return (
          <Panel key={agent.id} chrome={chrome} title={agent.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 600 }}>{agent.name}</div>
                <div style={{ color: chrome.secondary, fontSize: 14, wordBreak: 'break-word' }}>{agent.email}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em' }}>{agent.score}</div>
                <span style={{ ...chrome.chip, margin: 0, color: tone.color, background: tone.background }}>{agent.label}</span>
              </div>
            </div>
            <div style={{ color: chrome.secondary, fontSize: 14, marginTop: 10 }}>
              {agent.homeownersInvited} invited · {agent.homeownersActive} active · {agent.tasksCompleted} tasks · {agent.projectsSent} projects
              {agent.trialDaysLeft != null ? ` · ${agent.trialDaysLeft} trial days` : ''}
            </div>
            <div>
              {(agent.reasons || []).map((reason) => <span key={reason} style={chrome.chip}>{reason}</span>)}
            </div>
          </Panel>
        );
      })}
    </div>
  );
}
